import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import SiteHeader from "../components/SiteHeader";

export default function AdminCarousel() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [mode, setMode] = useState("add"); // "add" | "edit"
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title: "",
    caption: "",
    sort_order: 0,
    is_active: 1,
  });
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/api/admin/carousel");
      setItems(data || []);
      setErr("");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load carousel");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetForm = () => {
    setMode("add");
    setEditingId(null);
    setForm({
      title: "",
      caption: "",
      sort_order: 0,
      is_active: 1,
    });
    setImageFile(null);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    if (!form.title.trim() && !imageFile) {
      setErr("At least image or title is required");
      return;
    }

    try {
      setSaving(true);
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("caption", form.caption.trim());
      fd.append("sort_order", String(form.sort_order || 0));
      fd.append("is_active", String(form.is_active ? 1 : 0));
      if (imageFile) {
        fd.append("image", imageFile);
      }

      if (mode === "add") {
        await api.post("/api/admin/carousel", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.put(`/api/admin/carousel/${editingId}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      resetForm();
      await loadData();
      alert("Saved successfully");
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to save slide");
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (item) => {
    setMode("edit");
    setEditingId(item.id);
    setForm({
      title: item.title || "",
      caption: item.caption || "",
      sort_order: item.sort_order ?? 0,
      is_active: item.is_active ? 1 : 0,
    });
    setImageFile(null); // keep existing image unless user uploads new
  };

  const onDelete = async (item) => {
    if (!window.confirm("Mark this slide inactive?")) return;
    try {
      await api.delete(`/api/admin/carousel/${item.id}`);
      await loadData();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Failed to delete slide");
    }
  };

  const styles = {
    page: { minHeight: "100vh", background: "#f8fafc" },
    container: { maxWidth: 1100, margin: "0 auto", padding: 16 },
    card: {
      background: "#fff",
      borderRadius: 12,
      padding: 16,
      boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
      marginBottom: 16,
    },
    label: { display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 },
    input: {
      width: "100%",
      padding: "8px 10px",
      borderRadius: 8,
      border: "1px solid #cbd5e1",
      fontSize: 14,
    },
    textarea: {
      width: "100%",
      padding: "8px 10px",
      borderRadius: 8,
      border: "1px solid #cbd5e1",
      fontSize: 14,
      minHeight: 60,
    },
    buttonPrimary: {
      background: "#0f766e",
      color: "#fff",
      border: "none",
      borderRadius: 999,
      padding: "8px 16px",
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
    },
    buttonGhost: {
      background: "#e2e8f0",
      color: "#0f172a",
      border: "none",
      borderRadius: 999,
      padding: "8px 16px",
      fontSize: 14,
      fontWeight: 500,
      cursor: "pointer",
    },
    pill: (active) => ({
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      background: active ? "#d1fae5" : "#fee2e2",
      color: active ? "#166534" : "#991b1b",
      border: `1px solid ${active ? "#22c55e" : "#ef4444"}`,
    }),
  };

  return (
    <div style={styles.page}>
      <SiteHeader showAdmin />
      <div style={styles.container}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
            Home Carousel Images
          </h2>
          <button
            type="button"
            style={styles.buttonGhost}
            onClick={() => navigate("/dashboard")}
          >
            ← Back to Dashboard
          </button>
        </div>

        {/* Top: Form */}
        <div style={styles.card}>
          <h3 style={{ marginTop: 0, fontSize: 16, marginBottom: 8 }}>
            {mode === "add" ? "Add New Slide" : "Edit Slide"}
          </h3>

          {err && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{err}</div>}

          <form onSubmit={onSubmit}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.6fr 1.6fr 0.8fr 1fr",
                gap: 12,
                alignItems: "flex-start",
              }}
            >
              <div>
                <label style={styles.label}>Title</label>
                <input
                  style={styles.input}
                  name="title"
                  value={form.title}
                  onChange={onChange}
                  placeholder="Optional heading on image"
                />
              </div>
              <div>
                <label style={styles.label}>Caption</label>
                <textarea
                  style={styles.textarea}
                  name="caption"
                  value={form.caption}
                  onChange={onChange}
                  placeholder="Short caption (optional)"
                />
              </div>
              <div>
                <label style={styles.label}>Sort Order</label>
                <input
                  style={styles.input}
                  name="sort_order"
                  type="number"
                  value={form.sort_order}
                  onChange={onChange}
                />
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                  Lower number appears first
                </div>

                <div style={{ marginTop: 10 }}>
                  <label style={{ fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={!!form.is_active}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          is_active: e.target.checked ? 1 : 0,
                        }))
                      }
                    />{" "}
                    Active
                  </label>
                </div>
              </div>
              <div>
                <label style={styles.label}>
                  Image {mode === "add" && <span>(required)</span>}
                </label>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    setImageFile(file || null);
                  }}
                />
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                  Max 2 MB, PNG/JPG.
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: 16,
                display: "flex",
                gap: 8,
                alignItems: "center",
              }}
            >
              <button
                type="submit"
                style={styles.buttonPrimary}
                disabled={saving}
              >
                {saving
                  ? "Saving…"
                  : mode === "add"
                  ? "Add Slide"
                  : "Update Slide"}
              </button>
              {mode === "edit" && (
                <button
                  type="button"
                  style={styles.buttonGhost}
                  onClick={resetForm}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Bottom: List */}
        <div style={styles.card}>
          <h3 style={{ marginTop: 0, fontSize: 16, marginBottom: 8 }}>
            Existing Slides
          </h3>
          {loading && <div>Loading slides…</div>}
          {!loading && items.length === 0 && (
            <div style={{ fontSize: 13, color: "#64748b" }}>
              No slides yet. Add one above.
            </div>
          )}

          {!loading && items.length > 0 && (
            <div
              style={{
                display: "grid",
                gap: 12,
              }}
            >
              {items.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "0.6fr 1.8fr 0.8fr 0.8fr",
                    gap: 12,
                    alignItems: "center",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    padding: 8,
                  }}
                >
                  <div>
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.title || "Slide"}
                        style={{
                          width: "100%",
                          maxWidth: 140,
                          height: 70,
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1px solid #e5e7eb",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          maxWidth: 140,
                          height: 70,
                          borderRadius: 8,
                          background: "#e5e7eb",
                        }}
                      />
                    )}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    <div style={{ fontWeight: 600 }}>
                      {item.title || <span style={{ color: "#9ca3af" }}>No title</span>}
                    </div>
                    {item.caption && (
                      <div
                        style={{
                          marginTop: 2,
                          color: "#64748b",
                          maxHeight: 40,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.caption}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 13 }}>
                    <div>
                      <strong>Sort:</strong> {item.sort_order ?? 0}
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <span style={styles.pill(item.is_active === 1)}>
                        {item.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 4,
                    }}
                  >
                    <button
                      type="button"
                      style={{
                        ...styles.buttonGhost,
                        padding: "4px 10px",
                        fontSize: 12,
                      }}
                      onClick={() => onEdit(item)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      style={{
                        ...styles.buttonGhost,
                        padding: "4px 10px",
                        fontSize: 12,
                        background: "#fee2e2",
                        color: "#b91c1c",
                      }}
                      onClick={() => onDelete(item)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
